import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  Box, Card, CardContent, Typography, IconButton, Tooltip,
  Stack, Chip,
} from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { BRAND } from "../../theme";
import { useSectionAccent } from "../SectionAccentContext";

const ChartDataContext = createContext(() => {});

export function useChartData() {
  return useContext(ChartDataContext);
}

function escapeCSV(value) {
  if (value == null) return "";
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCSV(data, filename) {
  if (!data || !data.length) return;
  const headers = [...new Set(data.flatMap((row) => Object.keys(row)))];
  const rows = data.map((row) => headers.map((header) => escapeCSV(row[header])).join(","));
  const content = [headers.map(escapeCSV).join(","), ...rows].join("\r\n");
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename + ".csv";
  a.click();
  URL.revokeObjectURL(url);
}

async function downloadPNG(containerRef, filename) {
  const html2canvas = (await import("html2canvas")).default;
  const canvas = await html2canvas(containerRef.current, {
    backgroundColor: "#ffffff",
    scale: 2,
    useCORS: true,
  });
  const link = document.createElement("a");
  link.download = filename + ".png";
  link.href = canvas.toDataURL("image/png");
  link.click();
}

/** Botón de descarga con confirmación visual breve (check) al completar */
function DownloadButton({ title, Icon, accent, onClick }) {
  const [done, setDone] = useState(false);

  const handle = async () => {
    await onClick();
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };

  return (
    <Tooltip title={done ? "Descargado" : title}>
      <IconButton
        size="small"
        onClick={handle}
        sx={{
          color: done ? "#1B8A4B" : accent,
          bgcolor: done ? "#E7F5EC" : "transparent",
          transition: "all 0.15s ease",
          "&:hover": { bgcolor: done ? "#E7F5EC" : `${accent}14` },
        }}
      >
        {done ? <CheckIcon fontSize="small" /> : <Icon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

export default function ChartCard({
  title,
  subtitle,
  fuente,
  tag,
  children,
  csvData,
  csvFilename,
  pngFilename,
  minHeight = 320,
}) {
  const containerRef = useRef(null);
  const [registeredCsvData, setRegisteredCsvData] = useState();
  const { accent } = useSectionAccent();
  const fname = pngFilename || csvFilename || "grafico";
  const registerCsvData = useCallback((data) => {
    setRegisteredCsvData(Array.isArray(data) && data.length ? data : undefined);
  }, []);
  const downloadableData = Array.isArray(csvData) && csvData.length
    ? csvData
    : registeredCsvData;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": { boxShadow: "0 16px 36px -18px rgba(9,20,50,0.22)" },
      }}
    >
      {/* Barra superior con el acento de la sección */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${accent}, ${accent}55)` }} />

      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.75, gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            {tag && (
              <Chip
                label={tag}
                size="small"
                sx={{
                  bgcolor: `${accent}14`,
                  color: accent,
                  mb: 0.75,
                  fontSize: "0.7rem",
                  height: 22,
                  fontWeight: 700,
                }}
              />
            )}
            <Typography variant="h6" lineHeight={1.3} color={BRAND.text}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Botones descarga */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {downloadableData && (
              <DownloadButton
                title="Descargar CSV"
                Icon={TableChartOutlinedIcon}
                accent={accent}
                onClick={() => downloadCSV(downloadableData, fname)}
              />
            )}
            <DownloadButton
              title="Descargar PNG"
              Icon={ImageOutlinedIcon}
              accent={accent}
              onClick={() => downloadPNG(containerRef, fname)}
            />
          </Stack>
        </Box>

        {/* Gráfico */}
        <Box ref={containerRef} sx={{ minHeight, width: "100%", mt: 2, bgcolor: "#fff" }}>
          <ChartDataContext.Provider value={registerCsvData}>
            {children}
          </ChartDataContext.Provider>

          {/* Leyenda de autoría — siempre dentro del área capturada */}
          {fuente && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: BRAND.textFaint,
                textAlign: "right",
                mt: 1,
                fontStyle: "italic",
              }}
            >
              Elaborado por Fundación Nodo XXI. Fuente: {fuente}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
